/**
 * The languages the storefront speaks.
 *
 * Chosen by total speakers worldwide. Two substitutions from a strict ranking:
 * Nigerian Pidgin and Egyptian Arabic sit inside the top fifteen but have no
 * settled written UI convention, so Turkish and Italian take those slots —
 * Italian also being the second language of Buenos Aires by heritage.
 *
 * A flag is a country, not a language, and the two genuinely do not line up:
 * Arabic spans twenty-odd states, English and Spanish several each. The flag
 * is the visual handle people scan for; the authoritative label is `name`,
 * written in the language's own script.
 */
export type Locale =
  | 'es'
  | 'en'
  | 'zh'
  | 'hi'
  | 'fr'
  | 'ar'
  | 'bn'
  | 'pt'
  | 'ru'
  | 'ur'
  | 'id'
  | 'de'
  | 'ja'
  | 'tr'
  | 'it';

export interface LocaleMeta {
  code: Locale;
  /** Endonym — what speakers call the language themselves. */
  name: string;
  flag: string;
  dir: 'ltr' | 'rtl';
}

/**
 * Spanish leads: this is a café in Villa Crespo, and most visitors are local.
 *
 * Everything after it is ordered by how likely someone standing at this
 * counter is to actually pick it — who visits or lives in Argentina — not by
 * world speaker count (that ranking only decided which fifteen made the cut).
 * Brazil supplies Argentina's largest foreign-tourist flow by a wide margin;
 * English is the fallback almost any other international visitor reaches for;
 * Italian rides both real tourism (deep historical ties, common dual
 * citizenship) and the country's own Italian-descended majority; Chinese
 * reflects one of Argentina's largest resident immigrant communities — the
 * "supermercado chino" on nearly every block; German tracks Patagonia's
 * tourist trade plus its own historic settlements (Bariloche, Villa General
 * Belgrano); French is a steady if smaller tourist flow; Russian picked up
 * sharply from 2022 on, as documented arrivals seeking birthright citizenship
 * for their children; Arabic reflects the substantial Syrian-Lebanese-
 * descended community; Japanese has a long-standing but modest immigrant
 * community and light tourism. The last five are real presences with a
 * markedly smaller footprint here.
 */
export const LOCALES: readonly LocaleMeta[] = [
  { code: 'es', name: 'Español', flag: '🇦🇷', dir: 'ltr' },
  { code: 'pt', name: 'Português', flag: '🇧🇷', dir: 'ltr' },
  { code: 'en', name: 'English', flag: '🇬🇧', dir: 'ltr' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹', dir: 'ltr' },
  { code: 'zh', name: '中文', flag: '🇨🇳', dir: 'ltr' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', dir: 'ltr' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', dir: 'ltr' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺', dir: 'ltr' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦', dir: 'rtl' },
  { code: 'ja', name: '日本語', flag: '🇯🇵', dir: 'ltr' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳', dir: 'ltr' },
  { code: 'ur', name: 'اردو', flag: '🇵🇰', dir: 'rtl' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷', dir: 'ltr' },
  { code: 'bn', name: 'বাংলা', flag: '🇧🇩', dir: 'ltr' },
  { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩', dir: 'ltr' },
] as const;

export const DEFAULT_LOCALE: Locale = 'es';

const BY_CODE = new Map(LOCALES.map((l) => [l.code, l]));

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && BY_CODE.has(value as Locale);
}

export function localeMeta(code: Locale): LocaleMeta {
  return BY_CODE.get(code) ?? BY_CODE.get(DEFAULT_LOCALE)!;
}

/**
 * Picks the best of the browser's preferred languages.
 *
 * Only the base subtag is matched, so pt-BR, pt-PT and pt all land on
 * Portuguese, and zh-Hans, zh-TW and zh all land on Chinese. Ordered by the
 * visitor's own preference list rather than ours — navigator.languages is
 * already sorted by how much they want each one.
 */
export function detectLocale(
  languages: readonly string[] = typeof navigator === 'undefined'
    ? []
    : (navigator.languages ?? [navigator.language]),
): Locale {
  for (const tag of languages) {
    if (!tag) continue;
    const base = tag.toLowerCase().split('-')[0];
    if (isLocale(base)) return base;
  }
  return DEFAULT_LOCALE;
}
